import React from 'react';
import cx from 'classnames';
import {$generateNodesFromDOM} from '@lexical/html';
import {useLexicalComposerContext} from '@lexical/react/LexicalComposerContext';
import {useCallback, useEffect, useRef, useState} from 'react';
import {
    CAN_REDO_COMMAND,
    CAN_UNDO_COMMAND,
    REDO_COMMAND,
    UNDO_COMMAND,
    SELECTION_CHANGE_COMMAND,
    FORMAT_TEXT_COMMAND,
    FORMAT_ELEMENT_COMMAND,
    OUTDENT_CONTENT_COMMAND,
    INDENT_CONTENT_COMMAND,
    $getSelection,
    $isRangeSelection,
    $getRoot,
    $createTextNode,
    $isTextNode
} from 'lexical';
import {$isLinkNode, toggleLink} from '@lexical/link';
import {$getSelectionStyleValueForProperty, $patchStyleText} from '@lexical/selection';
import {$getNearestNodeOfType, mergeRegister} from '@lexical/utils';
import {
    INSERT_ORDERED_LIST_COMMAND,
    INSERT_UNORDERED_LIST_COMMAND,
    REMOVE_LIST_COMMAND,
    $isListNode,
    ListNode
} from '@lexical/list';
import {INSERT_HORIZONTAL_RULE_COMMAND} from '@lexical/react/LexicalHorizontalRuleNode';
import {FontDropDown} from './Font-dropdown/FontDropDown';
import useModal from '../../hooks/useModal';
import {InsertImageDialog} from '../ImagesPlugin/ImagesPlugin';
import {SavePlugin} from '../SavePlugin/SavePlugin';
import {getSelectedNode} from '../../utils/getSelectedNode';
import ColorPicker from '../../ui/ColorPicker/ColorPicker';
import {
    COMMAND_PRIORITY_LOW,
    DEFAULT_COLORS_MAP,
    FONT_FAMILY_OPTIONS,
    FONT_SIZE_OPTIONS
} from '../../constants';
import {FloatingLinkEditorPlugin} from '../../ui/LinkEditor/FloatingLinkEditor';
import {
    IconComment,
    IconHorLine,
    IconImage,
    IconIndent,
    IconLink,
    IconListOl,
    IconListUl,
    IconOutdent,
    IconRedo,
    IconTextBold,
    IconTextCenter,
    IconTextItalic,
    IconTextLeft,
    IconTextRight,
    IconTextStrikeThrough,
    IconTextUnderLine,
    IconUndo
} from './icons';
import type {NodeKey} from 'lexical';
import {ShortCodesPlugin} from '../ShortCodesPlugin';
import {
    ADD_LINK_COMMAND,
    EditLinkPayload,
    INSERT_COMMENT,
    INSERT_TAB_SPACES,
    OPEN_LINK_MODAL_COMMAND,
    SET_TEXT_COLOR
} from '../../commands';
import {EmojiPicker} from './EmojiPicker';
import {formsStore} from '../../../../../../../stores/Form/FormsStore';
import {prepareSignature} from '../../utils/prepareSignature';
import {texts} from '../../texts';
import {LinkDialog} from '../../ui/LinkDialog/LinkDialog';
import {getSelectionPoints} from '../../../../../../../utils/lexical/getSelectionPoints';
import {FontDropDownMode} from './Font-dropdown/enums';

const Divider = () => <div className="divider" />;

type ToolbarProps = {
    onSave: () => void;
};

export function ToolbarPlugin({onSave}: ToolbarProps) {
    const [editor] = useLexicalComposerContext();
    const toolbarRef = useRef(null);
    const [canUndo, setCanUndo] = useState(false);
    const [canRedo, setCanRedo] = useState(false);
    const [fontSize, setFontSize] = useState<string>('15px');
    const [modal, showModal] = useModal();
    const [linkModal, showLinkNodal] = useModal();
    const [fontFamily, setFontFamily] = useState<string>('Arial');
    const [blockType, setBlockType] = useState('paragraph');
    const [, setSelectedElementKey] = useState<NodeKey>('');
    const [isLink, setIsLink] = useState(false);
    const [isBold, setIsBold] = useState(false);
    const [isItalic, setIsItalic] = useState(false);
    const [isUnderline, setIsUnderline] = useState(false);
    const [isStrikethrough, setIsStrikethrough] = useState(false);
    const lastSelectedColor = useRef('#000000');

    const updateToolbar = useCallback(() => {
        const selection = $getSelection();
        if ($isRangeSelection(selection)) {
            const anchorNode = selection.anchor.getNode();
            const element =
                anchorNode.getKey() === 'root'
                    ? anchorNode
                    : anchorNode.getTopLevelElementOrThrow();
            const elementKey = element.getKey();
            const elementDOM = editor.getElementByKey(elementKey);
            if (elementDOM !== null) {
                setSelectedElementKey(elementKey);
                if ($isListNode(element)) {
                    const parentList = $getNearestNodeOfType(anchorNode, ListNode);
                    const type = parentList ? parentList.getTag() : element.getTag();
                    setBlockType(type);
                } else {
                    const type = element.getType();
                    setBlockType(type);
                }
            }
            // Update text format
            setIsBold(selection.hasFormat('bold'));
            setIsItalic(selection.hasFormat('italic'));
            setIsUnderline(selection.hasFormat('underline'));
            setIsStrikethrough(selection.hasFormat('strikethrough'));
            setFontSize($getSelectionStyleValueForProperty(selection, 'font-size', '14px'));
            setFontFamily($getSelectionStyleValueForProperty(selection, 'font-family', 'Arial'));

            // Update links
            const node = getSelectedNode(selection);
            const parent = node.getParent();
            if ($isLinkNode(parent) || $isLinkNode(node)) {
                setIsLink(true);
            } else {
                setIsLink(false);
            }
        }
    }, [editor]);

    useEffect(() => {
        const root = editor.getRootElement();
        const handle = e => {
            const href = e.target.parentElement.href;

            if (href) window.open(href, '_blank').focus();
        };
        root?.addEventListener('dblclick', handle);
        return () => root?.removeEventListener('dblclick', handle);
    }, [editor]);

    useEffect(() => {
        return mergeRegister(
            editor.registerUpdateListener(({editorState}) => {
                editorState.read(() => {
                    updateToolbar();
                });
            }),

            editor.registerCommand(
                ADD_LINK_COMMAND,
                (payload: EditLinkPayload) => {
                    const selection = $getSelection();

                    if ($isRangeSelection(selection)) {
                        // add a new link text
                        const linkContent = payload.text || payload.url;
                        const [start, end] = getSelectionPoints(selection);
                        const selectionNewEnd = start + linkContent.length;
                        const node = selection.anchor.getNode();

                        if (
                            !payload.update &&
                            selection.focus.key === selection.anchor.key &&
                            start === end
                        ) {
                            // New link creation without selection
                            selection.insertText(linkContent);
                            node.select(selection.anchor.offset, selectionNewEnd);
                            toggleLink(payload.url, payload);
                        } else {
                            // Updating existing link data or selected text data
                            if ($isTextNode(node)) {
                                const deleteCoords: [number, number] =
                                    start === end
                                        ? [0, node.getTextContent().length]
                                        : [start, end - start];
                                node.spliceText(...deleteCoords, linkContent);
                            } else {
                                node.getChildren().length;
                                node.splice(0, node.getChildren().length, [
                                    $createTextNode(linkContent)
                                ]);
                            }
                            node.select(start, selectionNewEnd);
                            toggleLink(payload.url, payload);
                        }
                    }
                    return false;
                },
                COMMAND_PRIORITY_LOW
            ),

            editor.registerCommand(
                OPEN_LINK_MODAL_COMMAND,
                () => {
                    const selection = $getSelection();

                    if ($isRangeSelection(selection)) {
                        const node = getSelectedNode(selection);
                        const parent = node.getParent();
                        const linkUpdate = $isLinkNode(parent) || $isLinkNode(node);

                        let defaultURL = '';
                        let defaultText = '';

                        if ($isLinkNode(parent)) {
                            defaultURL = parent.getURL();
                            defaultText = parent.getTextContent();
                        } else if ($isLinkNode(node)) {
                            defaultURL = node.getURL();
                            defaultText = node.getTextContent();
                        } else if (selection.focus.offset !== selection.anchor.offset) {
                            defaultText = selection.getTextContent();
                        }

                        showLinkNodal(
                            linkUpdate ? texts.edit_link_label : texts.add_link_label,
                            onClose => (
                                <LinkDialog
                                    defaultUrl={defaultURL}
                                    defaultText={defaultText}
                                    update={linkUpdate}
                                    onClose={onClose}
                                />
                            )
                        );
                    }

                    return false;
                },
                COMMAND_PRIORITY_LOW
            ),

            editor.registerCommand(
                SELECTION_CHANGE_COMMAND,
                _payload => {
                    updateToolbar();
                    return false;
                },
                COMMAND_PRIORITY_LOW
            ),
            editor.registerCommand(
                SELECTION_CHANGE_COMMAND,
                _payload => {
                    updateToolbar();
                    return false;
                },
                COMMAND_PRIORITY_LOW
            ),
            editor.registerCommand(
                CAN_UNDO_COMMAND,
                payload => {
                    setCanUndo(payload);
                    return false;
                },
                COMMAND_PRIORITY_LOW
            ),
            editor.registerCommand(
                CAN_REDO_COMMAND,
                payload => {
                    setCanRedo(payload);
                    return false;
                },
                COMMAND_PRIORITY_LOW
            ),
            editor.registerCommand(
                SET_TEXT_COLOR,
                (payload?: Record<string, string>) => {
                    const selection = $getSelection();
                    if ($isRangeSelection(selection)) {
                        const styles = payload || {color: lastSelectedColor.current};
                        lastSelectedColor.current = styles.color;
                        $patchStyleText(selection, styles);
                    }
                    return false;
                },
                COMMAND_PRIORITY_LOW
            ),
            editor.registerCommand(
                INSERT_COMMENT,
                () => {
                    editor?.update(() => {
                        const signatureData = formsStore.htmlSignature;

                        const parser = new DOMParser();
                        const dom = parser.parseFromString(
                            prepareSignature(signatureData),
                            'text/html'
                        );
                        const signatureNodes = $generateNodesFromDOM(editor, dom);
                        const cursorPositionNode = signatureNodes[1];
                        const children = $getRoot().getChildren();
                        $getRoot().append(...signatureNodes, ...children);

                        if (cursorPositionNode) {
                            // set cursor position to an empty node under the tag with Date info
                            cursorPositionNode.selectStart();
                        }
                    });
                    return false;
                },
                COMMAND_PRIORITY_LOW
            ),
            editor.registerCommand(
                INSERT_TAB_SPACES,
                () => {
                    editor?.update(() => {
                        const selection = $getSelection();
                        if (selection) {
                            selection.insertText('   ');
                        }
                    });
                    return false;
                },
                COMMAND_PRIORITY_LOW
            )
        );
    }, [editor, updateToolbar]);

    const addComment = useCallback(() => {
        editor.dispatchCommand(INSERT_COMMENT, undefined);
    }, [editor]);

    const addLink = useCallback(() => {
        editor.dispatchCommand(OPEN_LINK_MODAL_COMMAND, null);
    }, [editor]);

    const isBulletList = blockType === 'ul';
    const formatBulletList = useCallback(() => {
        const command = !isBulletList ? INSERT_UNORDERED_LIST_COMMAND : REMOVE_LIST_COMMAND;
        editor.dispatchCommand(command, undefined);
    }, [editor, isBulletList]);

    const isNumberList = blockType === 'ol';
    const formatNumberedList = useCallback(() => {
        const command = !isNumberList ? INSERT_ORDERED_LIST_COMMAND : REMOVE_LIST_COMMAND;
        editor.dispatchCommand(command, undefined);
    }, [editor, isNumberList]);

    const insertImage = useCallback(() => {
        showModal(texts.img_dialog_title, onClose => (
            <InsertImageDialog activeEditor={editor} onClose={onClose} />
        ));
    }, [editor]);

    const onFontColorSelect = useCallback((color?: string) => {
        return editor.dispatchCommand(SET_TEXT_COLOR, color ? {color} : undefined);
    }, []);

    return (
        <div className="priority-editor__toolbar" ref={toolbarRef}>
            <button
                title={texts.undo_btn_title}
                disabled={!canUndo}
                onClick={() => editor.dispatchCommand(UNDO_COMMAND, undefined)}
                className="priority-editor__toolbar-item spaced"
                aria-label="Undo"
            >
                <IconUndo className="format" />
            </button>
            <button
                disabled={!canRedo}
                onClick={() => editor.dispatchCommand(REDO_COMMAND, undefined)}
                className="priority-editor__toolbar-item"
                aria-label="Redo"
            >
                <IconRedo className="format" />
            </button>

            <Divider />

            <FontDropDown
                mode={FontDropDownMode.fontFamily}
                value={fontFamily}
                editor={editor}
                options={FONT_FAMILY_OPTIONS}
                label="Font"
            />
            <FontDropDown
                mode={FontDropDownMode.fontSize}
                value={fontSize}
                editor={editor}
                options={FONT_SIZE_OPTIONS}
                label="Size"
            />
            <ColorPicker
                colorsPalette={DEFAULT_COLORS_MAP}
                buttonClassName="priority-editor__toolbar-item color-picker"
                buttonAriaLabel="Formatting text color"
                color={lastSelectedColor.current}
                onChange={onFontColorSelect}
                title="text color"
            />

            <button
                title={texts.txt_color_btn_title}
                onClick={() => onFontColorSelect()}
                className={'priority-editor__toolbar-item spaced '}
                aria-label="Set previous color"
            >
                A
            </button>

            <Divider />

            <button
                title={texts.bold_btn_title}
                onClick={() => editor.dispatchCommand(FORMAT_TEXT_COMMAND, 'bold')}
                className={cx('priority-editor__toolbar-item spaced', {active: isBold})}
                aria-label="Format Bold"
            >
                <IconTextBold className="format" />
            </button>

            <button
                title={texts.italic_btn_title}
                onClick={() => editor.dispatchCommand(FORMAT_TEXT_COMMAND, 'italic')}
                className={cx('priority-editor__toolbar-item spaced', {active: isItalic})}
                aria-label="Format Italics"
            >
                <IconTextItalic className="format" />
            </button>

            <button
                title={texts.underline_btn_title}
                onClick={() => editor.dispatchCommand(FORMAT_TEXT_COMMAND, 'underline')}
                className={cx('priority-editor__toolbar-item spaced', {active: isUnderline})}
                aria-label="Format Underline"
            >
                <IconTextUnderLine className="format" />
            </button>

            <button
                onClick={() => editor.dispatchCommand(FORMAT_TEXT_COMMAND, 'strikethrough')}
                className={cx('priority-editor__toolbar-item spaced ', {active: isStrikethrough})}
                aria-label="Format Strikethrough"
            >
                <IconTextStrikeThrough className="format" />
            </button>

            <Divider />

            <button
                title={texts.bullets_btn_title}
                aria-label="Unordered List"
                className="priority-editor__toolbar-item spaced"
                onClick={formatBulletList}
            >
                <IconListUl className="format" />
            </button>

            <button
                title={texts.numbering_btn_title}
                aria-label="Numbered List"
                className="priority-editor__toolbar-item spaced"
                onClick={formatNumberedList}
            >
                <IconListOl className="format" />
            </button>

            <button
                title={texts.decrease_indent_btn_title}
                aria-label="Decrease indent"
                className="priority-editor__toolbar-item spaced"
                onClick={() => editor.dispatchCommand(OUTDENT_CONTENT_COMMAND, undefined)}
            >
                <IconOutdent className="format" />
            </button>

            <button
                title={texts.increase_indent_btn_title}
                aria-label="Increase indent"
                className="priority-editor__toolbar-item spaced"
                onClick={() => editor.dispatchCommand(INDENT_CONTENT_COMMAND, undefined)}
            >
                <IconIndent className="format" />
            </button>

            <Divider />

            <button
                title={texts.align_l_btn_title}
                onClick={() => editor.dispatchCommand(FORMAT_ELEMENT_COMMAND, 'left')}
                className="priority-editor__toolbar-item spaced"
                aria-label="Left Align"
            >
                <IconTextLeft className="format" />
            </button>

            <button
                title={texts.align_c_btn_title}
                onClick={() => editor.dispatchCommand(FORMAT_ELEMENT_COMMAND, 'center')}
                className="priority-editor__toolbar-item spaced"
                aria-label="Center Align"
            >
                <IconTextCenter className="format" />
            </button>

            <button
                title={texts.align_r_btn_title}
                onClick={() => editor.dispatchCommand(FORMAT_ELEMENT_COMMAND, 'right')}
                className="priority-editor__toolbar-item spaced"
                aria-label="Right Align"
            >
                <IconTextRight className="format" />
            </button>

            <Divider />

            <EmojiPicker />

            <button
                title={texts.hline_btn_title}
                onClick={() => editor.dispatchCommand(INSERT_HORIZONTAL_RULE_COMMAND, undefined)}
                className="priority-editor__toolbar-item spaced"
                aria-label="Horizontal line"
            >
                <IconHorLine className="format" />
            </button>

            <button
                title={texts.picture_btn_title}
                onClick={insertImage}
                className="priority-editor__toolbar-item spaced"
                aria-label="Image"
            >
                <IconImage />
            </button>

            <button
                title={texts.hyperlink_btn_title}
                onClick={addLink}
                className={cx('priority-editor__toolbar-item spaced', {active: isLink})}
                aria-label="Insert Link"
            >
                <IconLink className="format" />
            </button>

            <FloatingLinkEditorPlugin isLink={isLink} />

            <Divider />

            <button
                title={texts.comment_btn_title}
                onClick={addComment}
                className={'priority-editor__toolbar-item spaced '}
                aria-label="Add comment"
            >
                <IconComment className="format" />
            </button>

            <SavePlugin onSave={onSave} />

            <ShortCodesPlugin
                formatBulletList={formatBulletList}
                formatNumberedList={formatNumberedList}
                insertImage={insertImage}
            />

            {modal}
            {linkModal}
        </div>
    );
}
